import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, TextInput } from 'uxd-design-system-components';
import './WriteInModal.scss';
import WriteInModalProps from '~types/components/WriteInModalProps';
import LocaleContext from '../../contexts/LocaleContext';
import getText from '../../assets/locale/locale';

const WriteInModal = ({
  currentCardValue,
  setCurrentCardValue,
  showModal,
  closeModalHandler,
}: WriteInModalProps) => {
  const locId = useContext(LocaleContext);
  const MAX_LENGTH = 40;
  const [writeInValueError, setWriteInValueError] = useState(null);
  const [localWriteInValueText, setLocalWriteInValueText] = useState('');
  const [subText, setSubText] = useState(
    getText(locId, 'modalSubTextOne', 'writeInModal').replace(
      '%MAX_LENGTH%',
      MAX_LENGTH.toString()
    )
  );
  const [charsLeft, setCharsLeft] = useState(MAX_LENGTH);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);

  const writeInValueRegex = /^[a-zA-ZÀ-ÿ'’«»\s-]*$/;
  // this regex will match strings that contain all white spaces (space, tab, etc.)
  const whiteSpaceRegex = /^\s*$/;

  const invalidWriteInValue = getText(
    locId,
    'modalTextValueError',
    'writeInModal'
  );
  const missingWriteInValue = getText(
    locId,
    'modalMissingTextValueError',
    'writeInModal'
  );

  const validateForm = () => {
    let isValid = true;
    if (
      localWriteInValueText.length === 0 ||
      whiteSpaceRegex.test(localWriteInValueText)
    ) {
      setWriteInValueError(missingWriteInValue);
      setIsSubmitButtonDisabled(true);
      isValid = false;
    }
    setLocalWriteInValueText(localWriteInValueText.trim());
    return isValid;
  };

  const submitButtonHandler = () => {
    if (!writeInValueError && validateForm()) {
      closeModalHandler();
      setCurrentCardValue({
        ...currentCardValue,
        statementText: localWriteInValueText,
      });
    }
  };

  const validateWriteInValue = (text: string) => {
    if (text.length === 0 || whiteSpaceRegex.test(text)) {
      setIsSubmitButtonDisabled(true);
      setWriteInValueError(missingWriteInValue);
    } else if (!writeInValueRegex.test(text)) {
      setIsSubmitButtonDisabled(true);
      setWriteInValueError(invalidWriteInValue);
    } else {
      setIsSubmitButtonDisabled(false);
      setLocalWriteInValueText(text.trim());
      setWriteInValueError(null);
    }
  };

  const writeInValueHandler = (text: string) => {
    validateWriteInValue(text);
    setLocalWriteInValueText(text);
    setCharsLeft(MAX_LENGTH - text.length);
  };

  const buttons: Array<any> = [
    {
      text: getText(locId, 'cancelButton', 'writeInModal'),
      onClick: () => closeModalHandler(),
      styleVariant: 'secondary',
    },
    {
      text: getText(locId, 'submitButton', 'writeInModal'),
      onClick: () => submitButtonHandler(),
      styleVariant: 'primary',
      disabled: isSubmitButtonDisabled,
    },
  ];

  useEffect(() => {
    if (charsLeft === MAX_LENGTH) {
      setSubText(
        getText(locId, 'modalSubTextOne', 'writeInModal').replace(
          '%MAX_LENGTH%',
          MAX_LENGTH.toString()
        )
      );
    } else {
      setSubText(
        getText(locId, 'modalSubTextTwo', 'writeInModal').replace(
          '%CHARS_LEFT%',
          charsLeft.toString()
        )
      );
    }
  }, [charsLeft]);

  useEffect(() => {
    const statementText =
      currentCardValue.statementText.toLowerCase() !==
      getText(locId, 'writeInPlaceHolderText', 'writeInModal')
        ? currentCardValue.statementText
        : '';
    setLocalWriteInValueText(statementText);
    setCharsLeft(MAX_LENGTH - statementText.length);
    setIsSubmitButtonDisabled(false);
    setWriteInValueError(null);
  }, [currentCardValue]);

  return (
    <Dialog
      title={getText(locId, 'modalTitle', 'writeInModal')}
      open={showModal}
      buttons={buttons}
      className="writeInModal"
      onRequestClose={closeModalHandler}
    >
      <div>
        <p className="writeInModal--paragraph">
          {getText(locId, 'modalTextValuelabel', 'writeInModal')}
        </p>
      </div>
      <TextInput
        hideLabel
        id="write-in-input"
        label={getText(locId, 'modalTextValuelabel', 'writeInModal')}
        attributes={{ className: 'writeInModal--input' }}
        name="write-in-input"
        value={localWriteInValueText}
        maxLength={MAX_LENGTH}
        microcopy={subText}
        onChange={(text: string) => writeInValueHandler(text)}
        onBlur={validateWriteInValue}
        error={writeInValueError}
      />
    </Dialog>
  );
};

WriteInModal.propTypes = {
  currentCardValue: PropTypes.shape({
    statementId: PropTypes.number,
    statementText: PropTypes.string,
  }).isRequired,
  setCurrentCardValue: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  closeModalHandler: PropTypes.func.isRequired,
};

export default WriteInModal;
